import React from 'react';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Bug, Zap, Shield, Palette, Database } from 'lucide-react';
import { SectionHeader } from '@/components/SectionHeader';

const CHANGELOG = [
  {
    version: 'v5.1.0',
    date: '14 Jul 2026',
    title: 'War Room MTD Comparison, Client Tracker Automation, PI ↔ Tracker Sync, CSAT Respondents',
    tags: ['feature', 'improvement', 'bugfix'],
    isLatest: true,
    changes: [
      { type: 'feature', text: 'War Room HQ — "MTD vs Bulan Lepas" comparison table. Semua 9 channel side-by-side dengan % delta arrows (hijau=naik, merah=turun). Nampak dalam sekelip mata channel mana yang tak perform.' },
      { type: 'feature', text: 'Client Tracker — Pagination dropdown (15/30/50 per page) + First/Prev/Next/Last navigation footer. Auto-reset ke page 1 bila tukar filter.' },
      { type: 'feature', text: 'Client Tracker — Column "Date Added" baru sebelum Date Expiry. Concept: expiry = date added + 30 hari.' },
      { type: 'feature', text: 'Client Tracker — Auto-expire: mana-mana client yang tarikh expiry dah lepas + status active/prospect auto-flip jadi Expired. Jalan setiap kali page load, idempotent.' },
      { type: 'feature', text: 'Client Tracker — Inline status dropdown dalam table. Tak perlu tekan Edit — terus tukar status dari dropdown (Active/Prospect/Dormant/Churned/Expired).' },
      { type: 'feature', text: 'Product Installation — Column "Date Expiry" baru (auto = Date Added + 30 hari). Column "Date" rename kepada "Date Added". Auto-backfill untuk record lama pada GET pertama.' },
      { type: 'feature', text: 'Product Installation → Client Tracker Sync — Button "Sync ke Client Tracker" untuk transfer semua record sekali gus. Plus auto-sync setiap kali tambah/edit PI baru.' },
      { type: 'feature', text: 'PI Sync Merge Policy — Kalau client dah wujud (match by phone/email/account_id), isi fields kosong sahaja. Data existing dilindungi, tak overwrite.' },
      { type: 'feature', text: 'Creative Performance — SOY (Sembang Oh Yeah) channel kini muncul dalam Per-Channel Breakdown. Creative requests yang tag "sembang-ohh-yeahh" auto-attribute ke SOY.' },
      { type: 'feature', text: 'CSAT — Tab "Respondents" baru dengan senarai lengkap semua respondents (nama, email, phone, scores). Search + CSV export sekali klik.' },
      { type: 'feature', text: 'CSAT — Audit Trail untuk deleted responses. Setiap kali PM delete feedback client, snapshot & actor auto-log dalam Deletion History (tidak boleh delete secretly).' },
      { type: 'feature', text: 'CSAT — Delete individual response button (trash icon) dengan konfirmasi. Response deleted, tapi record kekal dalam audit trail.' },
      { type: 'feature', text: 'Garuda Channel Quick Log Parser — Support ordinal English (1st, 2nd, 3rd) dan bulan Melayu (Januari, Februari, dsb). Auto-extract trades dari text format harian.' },
      { type: 'improvement', text: 'Daily Data — Auto-navigate ke bulan semasa bila buka tab Marketing/Sales/Operation. Tak perlu tekan tab bulan satu-satu lagi.' },
      { type: 'bugfix', text: 'Yearly Snapshot — Target Commission line dulu tunjuk RM 130k, sekarang betul-betul reflect Admin > Scorecard > Target (~RM 750k untuk May 2026). Sum di IB-level, bukan STAFF descendants.' },
      { type: 'improvement', text: 'API Response Limit — CSAT results endpoint naik dari 200 → 1000 record. Semua feedback boleh nampak untuk large channels.' },
      { type: 'improvement', text: 'CSAT — Force English UI text pada public survey page (tradingfeedback.com). Consistency untuk international clients.' },
    ],
  },
  {
    version: 'v5.0.0',
    date: '14 Apr 2026',
    title: 'My Tasks Powerup, Weekly Standup Revamp, Summary Reports, Mobile Responsive',
    tags: ['feature', 'improvement'],
    changes: [
      { type: 'feature', text: 'My Tasks — Kanban Board view with 3 columns (Pending, In Progress, Completed). Drag tasks between columns.' },
      { type: 'feature', text: 'My Tasks — Timeline/Gantt view showing tasks across calendar days with color-coded bars.' },
      { type: 'feature', text: 'My Tasks — Quick Add: type title and press Enter to instantly create tasks without opening modal.' },
      { type: 'feature', text: 'My Tasks — Pin important tasks to top with highlighted card design (Underads red-orange gradient).' },
      { type: 'feature', text: 'My Tasks — Bulk Actions: select multiple tasks to mark done, delete, or move to project at once.' },
      { type: 'feature', text: 'My Tasks — Task Types: 9 categories (Brainstorming, Meeting, Design, Research, etc.) with icons.' },
      { type: 'feature', text: 'My Tasks — Task Dependencies: link tasks that depend on other tasks completion.' },
      { type: 'feature', text: 'My Tasks — Project folders with progress bars showing % completion per project.' },
      { type: 'feature', text: 'My Tasks — Subtasks checklist with per-item done/pending toggle.' },
      { type: 'feature', text: 'My Tasks — Comments: add notes and comments on any task.' },
      { type: 'feature', text: 'My Tasks — Due date color coding: red (overdue), amber (today), orange (3 days), grey (upcoming).' },
      { type: 'feature', text: 'My Tasks — Daily Focus section highlighting overdue and today\'s tasks at the top.' },
      { type: 'feature', text: 'Weekly Standup — Multi-point system: add individual bullet points for Done, Doing, Blockers.' },
      { type: 'feature', text: 'Weekly Standup — Edit after publish: can add new points and edit text (cannot delete published points).' },
      { type: 'feature', text: 'Weekly Standup — Report modal with week selector (W1-W4), stacked bar charts, department breakdown.' },
      { type: 'feature', text: 'Summary Reports — Channel Analytic report with ERR gauge, donut chart, sparklines, recommendations.' },
      { type: 'feature', text: 'Summary Reports — Overall Performance report with week selector, KPIs, channel rankings, trend charts.' },
      { type: 'feature', text: 'Auto Content Scoring — Telegram channels rated 1-10 based on posting frequency, engagement, content variety.' },
      { type: 'feature', text: 'Wins & Highlights Report — Automated positive achievements report with best day detection and milestones.' },
      { type: 'improvement', text: 'Sales Report Template — Standardized across all channels with Sales Pipeline + Sales Conversion + broker names from config.' },
      { type: 'improvement', text: 'Broker Names — Real names (Octa, HFM, Vantage) from channel config, not BrokerA/B/C.' },
      { type: 'improvement', text: 'Operation Report — Daily exact date only, not MTD aggregation. Win Rate included.' },
      { type: 'improvement', text: 'Issues & Optimization — 15+ detection rules with specific action plans for each issue.' },
      { type: 'improvement', text: 'Smart Report Scheduling — Auto-send at 12PM, retry 3PM, fallback 6PM if data not ready.' },
      { type: 'improvement', text: 'Admin Panel — Date picker for manual blast, "Wins & Highlights" message type added.' },
      { type: 'improvement', text: 'AI Agent — Now reads Telegram channel posts for content analysis. 20 diverse suggestion prompts with refresh.' },
      { type: 'improvement', text: 'Telegram Reminder — Link personal Telegram in Profile Settings. Test reminder sends pending task list.' },
      { type: 'improvement', text: 'Deposit data fix — Channel Overview now shows USD correctly (was inflated 4x showing MYR as USD).' },
      { type: 'improvement', text: 'Write Normalizer — All data saved to DB in consistent format. Prevents preview-vs-live data bugs.' },
      { type: 'improvement', text: 'Channel Analytic — Removed hardcoded FIRA data. Each channel now uses its own Telegram config.' },
      { type: 'improvement', text: 'Mobile Responsive — All IB detail pages (Profile, Overview, Performance, War Room, Analytic, Daily Data, Campaign, Activity) optimized for mobile.' },
      { type: 'improvement', text: 'Auto-logout extended from 30 minutes to 4 hours idle timeout.' },
      { type: 'improvement', text: 'Database Segmentation — New statuses: Recheck TV, Remove Access, Remove (replaced Expired).' },
    ],
  },
  {
    version: 'v4.1.0',
    date: '16 Mar 2026',
    title: 'Data Cleanup, Monthly Tabs, Creative Remarks',
    tags: ['improvement', 'feature'],
    changes: [
      { type: 'improvement', text: 'VIP Closed → Returning Closed — renamed across all pages (Daily Data, Channel Performance, Export).' },
      { type: 'improvement', text: 'Leads Consolidation — Marketing & IB leads columns removed. All leads now under Sales only. Data preserved.' },
      { type: 'improvement', text: 'Removed Leads Breakdown by Department section from Channel Overview (no longer needed).' },
      { type: 'improvement', text: 'Removed New/Prospect/VIP Leads cards from Marketing & Operation scorecards in Channel Performance.' },
      { type: 'feature', text: 'Monthly Tabs — My Tasks, Creative Request, Fund Request now have month selector. Stats reflect selected month. Carry-forward for incomplete tasks.' },
      { type: 'feature', text: 'Creative Request Remarks — New remarks field for additional notes. Visible in add, edit, and detail view.' },
    ],
  },
  {
    version: 'v4.0.0',
    date: '15 Mar 2026',
    title: 'AI Agent, Channel War Room, Automated Telegram Reports',
    tags: ['feature', 'improvement'],
    changes: [
      { type: 'feature', text: 'UA AI Agent — Tanya apa sahaja tentang data CRM. AI menganalisis semua data dan beri jawapan solid.' },
      { type: 'feature', text: 'AI Mode Toggle — Quick (1 AI, jimat kredit) atau Deep Analysis (3 AI gabungan untuk jawapan komprehensif).' },
      { type: 'feature', text: 'AI Agent Pin & Folders — Pin jawapan penting. Buat folder untuk susun chat sessions by channel.' },
      { type: 'feature', text: 'Channel War Room — Diagnostik lengkap untuk setiap channel: Health Score, Auto-Detected Issues, Key Metrics, 14-Day Trends, Telegram Stats, Signal Performance.' },
      { type: 'feature', text: 'War Room Optimization Engine — AI auto-generate strategi terperinci (5 tactics setiap satu) berdasarkan isu yang dikesan. Add to Log dengan 1 klik.' },
      { type: 'feature', text: 'War Room HQ — Command Center dalam IB Management. Semua channel dalam satu pandangan: scoreboard, critical alerts, channel ranking table, mini sparklines.' },
      { type: 'feature', text: 'Automated Telegram Reports — Bot hantar daily/weekly/monthly report ke Telegram group setiap channel. 4 mesej berasingan: Sales, Marketing, Operations, Issues & Optimization.' },
      { type: 'feature', text: 'TG Reports Admin Panel — Pilih channel, pilih jenis message, send manual. History log dengan filter.' },
      { type: 'improvement', text: 'Quick Summary — Auto-generated ringkasan status channel di War Room.' },
      { type: 'improvement', text: 'Month Comparison — Side-by-side table bulan ini vs bulan lepas (9 metrics).' },
      { type: 'improvement', text: 'War Room → Log Integration — Optimization strategies auto-masuk Log dengan field mapping betul (strategy → description, tactics → action taken).' },
      { type: 'improvement', text: 'Unified Health Score — Satu formula untuk List of IB dan War Room. Achievement (60%) + Process Health (40%).' },
      { type: 'improvement', text: 'Markdown Typography — Proper heading hierarchy (H1/H2/H3), numbered lists, bullet points, paragraph spacing dalam AI Agent.' },
    ],
  },
  {
    version: 'v3.0.0',
    date: '10 Mar 2026',
    title: 'Notification System, Inbox DM, @Mention, Admin Reset Password',
    tags: ['feature', 'improvement'],
    changes: [
      { type: 'feature', text: 'Notification System — 3 tabs: All, Activity, Mention. Real-time badge count.' },
      { type: 'feature', text: 'Inbox Direct Message — 1-to-1 chat antara team members. Icon berasingan dari notification.' },
      { type: 'feature', text: '@Mention — Type @ dalam mana-mana comment field untuk mention user. @all untuk notify semua.' },
      { type: 'feature', text: 'User Profile Popup — Klik user online untuk lihat profile ringkas + Send Message.' },
      { type: 'feature', text: 'Admin Reset Password — Button di Admin Panel untuk reset password user yang lupa.' },
      { type: 'improvement', text: 'Click-outside-to-close — Klik tempat kosong untuk tutup notification/inbox panel.' },
      { type: 'improvement', text: 'Notification auto-trigger untuk semua action: comment, assign, mention, poll, announcement, fund request, standup, feedback.' },
    ],
  },
  {
    version: 'v2.9.0',
    date: '9 Mar 2026',
    title: 'Weekly Standup Enhancement, Leaderboard Fix, Voting Date',
    tags: ['feature', 'improvement'],
    changes: [
      { type: 'feature', text: 'Weekly Standup — Weekly grouping (Week 1-4), status per standup (Pending/In Progress/Completed), month navigation.' },
      { type: 'improvement', text: 'Leaderboard — Scoring dari Tasks, Ideas, Standups, Creative Completed, Mood check-in. Avatar dan department.' },
      { type: 'feature', text: 'Voting & Polls — Date dan time created dipaparkan di setiap poll.' },
      { type: 'improvement', text: 'Finance — Disbursed date, disbursed by name sekarang disimpan dan dipaparkan.' },
    ],
  },
  {
    version: 'v2.8.0',
    date: '5 Mar 2026',
    title: 'Creative Request Classic View, Multi-PIC, Status Update',
    tags: ['feature', 'improvement'],
    changes: [
      { type: 'feature', text: 'Classic View — Monday.com style table view untuk Creative Request. Columns: Title, Description, Channel, Materials, PIC, Status, dll.' },
      { type: 'feature', text: 'Multi-PIC — Boleh assign 2-3 PIC untuk satu request. Semua PIC nampak dalam My Tasks > Assign to Me.' },
      { type: 'improvement', text: 'Status — "Pending" ditukar ke "Not Started". Tambah "Amendment". Total 5 status.' },
      { type: 'improvement', text: 'PIC boleh edit — PIC yang di-assign boleh update status, tambah completed link, upload file.' },
      { type: 'improvement', text: 'Materials — Simplified ke 3 pilihan: Poster, Video, Others (dengan custom input). Filter jadi button tabs.' },
    ],
  },
  {
    version: 'v2.7.0',
    date: '3 Mar 2026',
    title: 'Channel Analytic MTProto, Yearly Target Auto-Divide, RBAC Fix',
    tags: ['feature', 'fix'],
    changes: [
      { type: 'feature', text: 'MTProto untuk semua channel — Real joined/left, notification rate, views by source dari Telegram.' },
      { type: 'improvement', text: 'Growth Analysis — Papar semua hari (bukan 4-5 je). Missing dates di-interpolate.' },
      { type: 'feature', text: 'Yearly Target → Auto divide 12 bulan. Adjust bulan tertentu → yearly auto recalculate.' },
      { type: 'feature', text: 'Aggregated Targets — Performance Projections guna real target dari Channel Settings (bukan hardcoded).' },
      { type: 'fix', text: 'RBAC Fix — Channel-restricted users sekarang nampak Team Space bila admin explicitly enable.' },
      { type: 'improvement', text: 'Resource Library — Download fix, preview fix, tambah download button di grid dan list view.' },
      { type: 'feature', text: 'Sales Closing Rate — Auto-calculated column (Total Closed / Total Leads × 100) di Daily Data.' },
    ],
  },
  {
    version: 'v2.6.0',
    date: '17 Feb 2026',
    title: 'Export Center, Likes & Comments, Real KPI Trends',
    tags: ['feature', 'improvement'],
    screenshot: 'https://static.prod-images.emergentagent.com/jobs/9796b7f1-7bb9-40dd-9e85-a21b33a55105/images/7cd27ed48fa99006d95e2a7c607b4b51c7fd0f1e3ce12b8cb125a1b9dea9b4a4.png',
    changes: [
      { type: 'feature', text: 'Export Center — download Daily Data, Performance, IB List dalam Excel/PDF' },
      { type: 'feature', text: 'Announcement Likes & Comments — boleh like dan komen pada setiap announcement' },
      { type: 'improvement', text: 'Dashboard KPI Cards — trend % sekarang compare real data bulan ini vs bulan lepas (bukan hardcoded)' },
      { type: 'feature', text: 'What\'s New page — changelog dan version history' },
    ],
  },
  {
    version: 'v2.5.0',
    date: '16 Feb 2026',
    title: 'RBAC, Channel-Specific Access, Finance Module',
    tags: ['feature', 'security'],
    changes: [
      { type: 'feature', text: 'Finance Module — Fund Request (submit/approve/reject) + Marketing Fund tracking per channel' },
      { type: 'feature', text: 'RBAC — Per-user permission toggles dalam Admin Panel (Dashboard, IB, Finance, Admin, dll)' },
      { type: 'feature', text: 'Channel-Specific Access — outsource team hanya nampak channel yang ditugaskan' },
      { type: 'improvement', text: 'Admin Panel — Permissions modal popup (ganti inline checkboxes)' },
    ],
  },
  {
    version: 'v2.4.0',
    date: '13 Feb 2026',
    title: 'Sticky Header, Mobile Responsive, Edit History',
    tags: ['improvement', 'fix'],
    changes: [
      { type: 'improvement', text: 'Sticky Header — search, notification, avatar kekal di atas bila scroll' },
      { type: 'improvement', text: 'Global Header dalam IB Details page' },
      { type: 'feature', text: 'Edit History — klik "Diedit oleh" untuk lihat log siapa ADD/EDIT/DELETE data' },
      { type: 'improvement', text: 'Mobile responsive — Dashboard, IB List, IB Details semua adjusted untuk mobile view' },
      { type: 'fix', text: 'Copy paste — diaktifkan semula (sebelum ini diblock oleh security.js)' },
    ],
  },
  {
    version: 'v2.3.0',
    date: '12 Feb 2026',
    title: 'Data Persistence Fix, Broker Chart, Health Score',
    tags: ['fix', 'improvement'],
    changes: [
      { type: 'fix', text: 'CRITICAL: Data hilang selepas refresh — auto-save race condition fixed untuk semua components' },
      { type: 'fix', text: 'Broker Config — custom broker names sekarang persist selepas refresh' },
      { type: 'fix', text: 'Channel Performance — blank hitam fixed (TDZ error)' },
      { type: 'improvement', text: 'Health Score — real calculation dari commission, active client, traded lot, deposit' },
      { type: 'improvement', text: 'IB List — full numbers (bukan abbreviated RM51.2k)' },
      { type: 'improvement', text: 'Broker Performance Chart — hanya tunjuk broker yang aktif dalam config' },
    ],
  },
  {
    version: 'v2.2.0',
    date: '12 Feb 2026',
    title: 'My Tasks, Creative Request, Daily Data Enhancements',
    tags: ['feature'],
    changes: [
      { type: 'feature', text: 'My Tasks — Status dropdown (Pending/In Progress/Completed) pada setiap task' },
      { type: 'feature', text: 'Creative Request — Materials filter, "Sembang Ohh Yeahh" channel, multiple PIC' },
      { type: 'feature', text: 'Daily Data Operation — Signal Generated field (Livetrade/Mapping/Others)' },
      { type: 'feature', text: 'Announcement — File attachment (PDF, Word, Excel)' },
      { type: 'improvement', text: 'Assigned To Me — search bar, status filter, grid view' },
    ],
  },
  {
    version: 'v2.1.0',
    date: '12 Feb 2026',
    title: 'Channel Analytic, Telegram Scraper, Import Fixes',
    tags: ['feature', 'fix'],
    changes: [
      { type: 'feature', text: 'Telegram Scraper — real reactions, polls, forwards dari t.me embed pages' },
      { type: 'fix', text: 'Excel import — parse_date() handle datetime objects (full 15 rows imported)' },
      { type: 'fix', text: 'Channel Analytic — poll rate 0% instead of "Pending"' },
      { type: 'fix', text: 'Date picker — Channel Overview/Performance fetch ALL months correctly' },
      { type: 'fix', text: 'File import "Network Error" — missing get_exchange_rate_for_month function' },
    ],
  },
  {
    version: 'v2.0.0',
    date: '11 Feb 2026',
    title: 'MongoDB Migration, WebSocket, Data Consistency',
    tags: ['improvement'],
    changes: [
      { type: 'improvement', text: 'All pages migrated from localStorage to MongoDB — data persist across sessions' },
      { type: 'feature', text: 'WebSocket real-time notifications dan auto-refresh' },
      { type: 'improvement', text: 'Commission calculation standardized to MYR across all pages' },
      { type: 'improvement', text: 'Brand logo stored in MongoDB (persist across deployments)' },
      { type: 'feature', text: '"Last Edited By" tracking — shows who edited and when' },
    ],
  },
];

const typeConfig = {
  feature: { label: 'New', icon: Sparkles, bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  improvement: { label: 'Improved', icon: Zap, bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  fix: { label: 'Fixed', icon: Bug, bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
};

export default function ChangelogPage() {
  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader icon={Sparkles} title="What's New" subtitle="Version history dan changelog" />

        <div className="max-w-3xl mx-auto mt-6 space-y-6">
          {CHANGELOG.map((release, idx) => (
            <div key={release.version} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              {/* Version Header */}
              <div className={`p-5 ${idx === 0 ? 'bg-gradient-to-r from-[hsl(var(--primary))]/10 to-transparent border-b border-[hsl(var(--primary))]/20' : 'border-b border-[hsl(var(--border))]'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Badge className={`${idx === 0 ? 'bg-[hsl(var(--primary))] text-white' : 'bg-[hsl(var(--surface))] text-[hsl(var(--text-secondary))]'} text-sm font-mono px-3 py-1`}>{release.version}</Badge>
                    {idx === 0 && <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px]">LATEST</Badge>}
                  </div>
                  <span className="text-xs text-[hsl(var(--text-muted))]">{release.date}</span>
                </div>
                <h3 className="font-semibold text-base">{release.title}</h3>
              </div>
              
              {/* Screenshot */}
              {release.screenshot && (
                <div className="px-5 pb-2">
                  <div className="rounded-lg overflow-hidden border border-[hsl(var(--border))]">
                    <img src={release.screenshot} alt={`${release.version} preview`} className="w-full h-48 object-cover" />
                  </div>
                </div>
              )}
              
              {/* Changes */}
              <div className="p-5 space-y-2.5">
                {release.changes.map((change, ci) => {
                  const cfg = typeConfig[change.type] || typeConfig.improvement;
                  const Icon = cfg.icon;
                  return (
                    <div key={ci} className="flex items-start gap-3">
                      <Badge className={`${cfg.bg} ${cfg.text} border ${cfg.border} text-[9px] mt-0.5 shrink-0 w-16 justify-center`}>
                        <Icon size={10} className="mr-1" />{cfg.label}
                      </Badge>
                      <span className="text-sm text-[hsl(var(--text-secondary))]">{change.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </ResponsiveLayout>
  );
}
